import { z } from 'zod';
import { HordeIntentSchema } from './horde-events';
import {
    HostSecretVaultStateSchema,
    PersistedHordeGameSchema,
    PlayerSecretViewStateSchema,
    PublicHistoryEntrySchema,
} from './horde-session';

const IdSchema = z.string().min(1).max(128);
const TimestampSchema = z.iso.datetime({ offset: true });

/** The version of the durable Horde envelope, independent from RxDB's collection schema version. */
export const HORDE_PERSISTENCE_DOCUMENT_VERSION = 3 as const;

const HordePersistenceVersionSchema = z
    .literal(HORDE_PERSISTENCE_DOCUMENT_VERSION)
    .default(HORDE_PERSISTENCE_DOCUMENT_VERSION);

export const HordeAuthorityDocumentSchema = z
    .object({
        documentVersion: HordePersistenceVersionSchema,
        game: PersistedHordeGameSchema,
        gameId: IdSchema,
        vault: HostSecretVaultStateSchema,
    })
    .strict();
export type HordeAuthorityDocument = z.infer<typeof HordeAuthorityDocumentSchema>;

export const HordePlayerSecretViewDocumentSchema = PlayerSecretViewStateSchema.extend({
    documentId: IdSchema,
    documentVersion: HordePersistenceVersionSchema,
}).strict();
export type HordePlayerSecretViewDocument = z.infer<typeof HordePlayerSecretViewDocumentSchema>;

export const HordeGameEventDocumentSchema = PublicHistoryEntrySchema.extend({
    actorDeviceId: IdSchema,
    documentVersion: HordePersistenceVersionSchema,
    gameId: IdSchema,
    privatePlayerId: IdSchema.nullable(),
    visibility: z.enum(['public', 'privatePlayer']),
}).strict();
export type HordeGameEventDocument = z.infer<typeof HordeGameEventDocumentSchema>;

export const HordeGameIntentDocumentSchema = z
    .object({
        acceptedRevision: z.number().int().positive().nullable(),
        actorDeviceId: IdSchema,
        actorUserId: IdSchema,
        createdAt: TimestampSchema,
        documentVersion: HordePersistenceVersionSchema,
        event: HordeIntentSchema,
        eventType: IdSchema,
        expectedRevision: z.number().int().nonnegative(),
        gameId: IdSchema,
        intentId: IdSchema,
        outboxScopeId: IdSchema,
        rejectionReason: z.string().min(1).nullable(),
        status: z.enum(['pending', 'accepted', 'rejected']),
        updatedAt: TimestampSchema,
        visibility: z.enum(['public', 'privatePlayer']),
    })
    .strict()
    .superRefine((document, context) => {
        const metadata = document.event.metadata;
        const expectedVisibility = typeof metadata.visibility === 'string' ? 'public' : 'privatePlayer';
        const comparisons: ReadonlyArray<[string, unknown, unknown]> = [
            ['actorDeviceId', document.actorDeviceId, metadata.actorDeviceId],
            ['actorUserId', document.actorUserId, metadata.actorUserId],
            ['createdAt', document.createdAt, metadata.createdAt],
            ['eventType', document.eventType, document.event.type],
            ['expectedRevision', document.expectedRevision, metadata.expectedRevision],
            ['gameId', document.gameId, metadata.gameId],
            ['intentId', document.intentId, metadata.eventId],
            ['visibility', document.visibility, expectedVisibility],
        ];

        for (const [path, actual, expected] of comparisons) {
            if (actual !== expected) {
                context.addIssue({
                    code: 'custom',
                    message: `Must match event metadata (${String(expected)}).`,
                    path: [path],
                });
            }
        }

        if (document.status === 'accepted' && document.acceptedRevision === null) {
            context.addIssue({
                code: 'custom',
                message: 'Accepted intents require an accepted revision.',
                path: ['acceptedRevision'],
            });
        }

        if (document.status === 'rejected' && document.rejectionReason === null) {
            context.addIssue({
                code: 'custom',
                message: 'Rejected intents require a rejection reason.',
                path: ['rejectionReason'],
            });
        }
    });
export type HordeGameIntentDocument = z.infer<typeof HordeGameIntentDocumentSchema>;

/** Semantic name for pending documents that have passed the primary intent-document schema. */
export type ValidatedHordeGameIntentDocument = z.infer<typeof HordeGameIntentDocumentSchema>;

export const HordeCommitDocumentSchema = z
    .object({
        authorityRevision: z.number().int().positive().nullable(),
        commitId: IdSchema,
        documentVersion: HordePersistenceVersionSchema,
        gameId: IdSchema,
        intent: HordeIntentSchema,
        status: z.enum(['prepared', 'projectionPending', 'projected']),
        updatedAt: TimestampSchema,
    })
    .strict();
export type HordeCommitDocument = z.infer<typeof HordeCommitDocumentSchema>;

/** Builds the stable persistence id for one player’s private Horde projection. */
export function hordePlayerSecretViewDocumentId(gameId: string, playerId: string): string {
    return `${gameId}:${playerId}`;
}
